import React, { useEffect, useRef, useState } from "react";

type Slide = {
    id: string;
    title: string;
    image: string;
    overlay: string;
};

const SLIDES: Slide[] = [
    {
        id: "heat-stretch",
        title: "Heat &\nStretch",
        image: "/img/features-heat-stretch.png",
        overlay: "linear-gradient(180deg, rgba(142,236,211,0.4), rgba(162,165,255,0.4))",
    },
    {
        id: "sweat",
        title: "Sweat\nEnergize\nPower Up",
        image: "/img/features-sweat.png",
        overlay: "linear-gradient(180deg, rgba(84,143,185,0.55), rgba(101,179,169,0.55))",
    },
    {
        id: "intensive",
        title: "Intensive Cardio\nStrengthening Core",
        image: "/img/features-intensive.png",
        overlay: "linear-gradient(180deg, rgba(248,113,79,0), rgba(248,209,79,0.4))",
    },
    {
        id: "relax",
        title: "Relax\nRecover\nRepeat",
        image: "/img/features-relax.png",
        overlay: "linear-gradient(180deg, rgba(168,221,63,0), rgba(63,213,221,0.4))",
    },
];

const TRANSITION = 500;
const CLONE_COUNT = 5;

export default function FeaturesSliderSection() {
    const base = SLIDES.length;

    // 🔁 клонирование
    const slides = [
        ...SLIDES.slice(-CLONE_COUNT),
        ...SLIDES,
        ...SLIDES.slice(0, CLONE_COUNT),
    ];

    const [ready, setReady] = useState(false);
    const [index, setIndex] = useState(CLONE_COUNT);
    const [animate, setAnimate] = useState(true);

    const trackRef = useRef<HTMLDivElement>(null);
    const slideWidth = useRef(0);
    const animatingRef = useRef(false);

    // 📏 измеряем ширину
    useEffect(() => {
        const measure = () => {
            const el = trackRef.current?.children[0] as HTMLElement;
            if (!el) return;

            slideWidth.current = el.offsetWidth;

            // 👉 сразу выставляем позицию БЕЗ анимации
            setAnimate(false);
            setIndex(CLONE_COUNT);

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setAnimate(true);
                    setReady(true); // теперь можно кликать
                });
            });
        };

        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, []);

    useEffect(() => {
        SLIDES.forEach((slide) => {
            const img = new Image();
            img.src = slide.image;
        });
    }, []);

    // 👉 движение
    const move = (dir: -1 | 1) => {
        if (animatingRef.current) return;

        animatingRef.current = true;
        setIndex((prev) => prev + dir);
    };

    // 🔁 infinite loop
    const onTransitionEnd = () => {
        animatingRef.current = false;

        if (index < CLONE_COUNT) {
            setAnimate(false);
            setIndex(index + base);
        }

        if (index >= base + CLONE_COUNT) {
            setAnimate(false);
            setIndex(index - base);
        }
    };

    useEffect(() => {
        if (!animate) {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setAnimate(true);
                });
            });
        }
    }, [animate]);

    // 👉 возвращаем transition
    useEffect(() => {
        if (!animate) {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setAnimate(true);
                });
            });
        }
    }, [animate]);

    const translateX = slideWidth.current ? -(index * slideWidth.current) : 0;

    const realIndex = ((index - CLONE_COUNT) % base + base) % base;
    const counter = `${realIndex + 1}/${base}`;

    return (
        <section className="bg-white overflow-hidden">
            <div className="mx-auto w-full">

                {/* viewport */}
                <div className="overflow-hidden h-[220px] sm:h-[280px] lg:h-[340px] desk:h-[384px]">

                    {/* track */}
                    <div
                        ref={trackRef}
                        onTransitionEnd={onTransitionEnd}
                        className="flex h-full"
                        style={{
                            transform: `translateX(${translateX}px)`,
                            transition: animate
                                ? `transform ${TRANSITION}ms ease`
                                : "none",
                        }}
                    >
                        {slides.map((s, i) => (
                            <div
                                key={`${s.id}-${i}`}
                                className="relative h-full shrink-0
                                    w-[92vw]
                                    sm:w-[48vw]
                                    lg:w-[33.333vw]
                                    desk:w-[624px]"
                            >
                                <img
                                    src={s.image}
                                    loading="eager"
                                    decoding="async"
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                                <div
                                    className="absolute inset-0"
                                    style={{background: s.overlay}}
                                />
                                <div className="absolute left-[20px] top-[18px] whitespace-pre-line text-white">
                                    {s.title}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* controls */}
                <div className="flex items-center justify-center gap-[16px] py-[12px]">
                    <button onClick={() => move(-1)}>←</button>
                    <div>{counter}</div>
                    <button onClick={() => move(1)}>→</button>
                </div>
            </div>
        </section>
    );
}